import { useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../../api/index.js';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Divider,
  Switch,
  Stack,
  Chip,
  Checkbox,
  IconButton 
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CachedIcon from '@mui/icons-material/Cached';
import SyncDisabledIcon from '@mui/icons-material/SyncDisabled';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HeaderHandlerTool from '../components/HeaderHandlerTool.js';

const DEFAULT_SCOPE = 'default';

const prettyMode = (m) => {
  if (m === "on_access") return "On access";
  if (m === "hourly") return "Hourly (on access if >= 1h)";
  return m || "-";
};

const ttlLine = (label, override, fallback) => {
  const hasOverride = override !== null && override !== undefined && override !== "";
  const value = hasOverride ? override : fallback;
  const origin = hasOverride ? "override" : "default";
  return { label, value, origin };
};

// const ChipBool = ({ on, labelOn, labelOff }) => (
//   <Stack direction="row" alignItems="center" gap={1}>
//     <Switch checked={!!on} disabled size="small" />
//     <Typography variant="body2">{on ? labelOn : labelOff}</Typography>
//   </Stack>
// );

const fmtDate = (value) => {
  if (!value) return "-";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";
    return d.toLocaleString(); // respecte la timezone du navigateur
  } catch {
    return "-";
  }
};

const fmtDateTime = (value) => {
  if (!value) return "-";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "-";

    return d.toLocaleString('fr-FR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return "-";
  }
};

  //helper durée de traitement - plus propre
  const fmtDuration = (ms) => {
    if (!Number.isFinite(ms)) return "-";
    if (ms < 1000) return `${ms} ms`;
    return `${(ms / 1000).toFixed(1)} s`;
  };


const getInfoObject = (info) => {
  if (!info) return null;
  // console.log('typeof info', typeof info)
  if (typeof info === "object") return info;
  if (typeof info === "string") {
    try {
      return JSON.parse(info);
    } catch {
      return null;
    }
  }
  return null;
};



const buildContextSummary = (row) => {
  const ctx = row?.context || {};

  const infoObj = getInfoObject(ctx.info);
  // console.log('infoObj', infoObj)
  let infos = infoObj?.phase?.name || '';
  //sportevent\":{\"beginDate\":\"2025-07-25T22:00:00.000Z\",\"endDate\":\"2025-07-26T21:59:59.999Z\"}
  infos += infoObj?.sportevent?.beginDate ? ` [du ${fmtDateTime(infoObj.sportevent.beginDate)}]` : '';
  infos += infoObj?.sportevent?.endDate ? ` [au ${fmtDateTime(infoObj.sportevent.endDate)}]` : '';

  // console.log('ctx = ', ctx)

  let externalKeys = null;

   if(ctx.externalKey && typeof ctx.externalKey === "object"){
      const entries = Object.entries(ctx.externalKey).map(([k, v]) => `${k}:${v}`).join(", ");
      externalKeys = entries;
    }

  const parts = [
    ctx.liveId ? `live: ${ctx.liveId}` : null,
    ctx.configId ? `type: ${ctx.configId}` : null,
    ctx.pageId ? `page: ${ctx.pageId}` : null,
    ctx.viewId ? `vue: ${ctx.viewId}` : null,
    ctx.tabId ? `onglet: ${ctx.tabId}` : null,
    //externalKey is an object, we need to set key:value
    externalKeys ? `${externalKeys}` : null,
    
    infos ? `${infos}` : null,
  ].filter(Boolean);

  // console.log('ctx2 = ', ctx)

  return parts.join(" • ");
};

const CacheManager = () => {
  const isMountedRef = useRef(true);

  // ---- Lives (pour autocomplétion) ----
  const [lives, setLives] = useState([]);

  // ---- Default TTLs (global) ----
  const [defaultTtls, setDefaultTtls] = useState({ freshTtlSec: 60, staleTtlSec: 3600, refreshMode: "on_access", archived: false });

  // ---- Filters ----
  const [whatId, setWhatId] = useState(''); // liveId
  const [scope, setScope] = useState(DEFAULT_SCOPE);

  // ---- Data ----
  const [configs, setConfigs] = useState([]);
  const [caches, setCaches] = useState([]);
  const [deletingCacheId, setDeletingCacheId] = useState(null);

  // ---- UI ----
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [loadingCaches, setLoadingCaches] = useState(false);

  // ---- Form (upsert) ----
  const [form, setForm] = useState({
    freshTtlSec: '',
    staleTtlSec: '',
    refreshMode: 'on_access',
    archived: false,
  });


  const [selectedConfig, setSelectedConfig] = useState(null);

  const [refreshReport, setRefreshReport] = useState(null);
  const [refreshingNow, setRefreshingNow] = useState(false);
  const [planningRefresh, setPlanningRefresh] = useState(false);

  // Rafraichissement unitaire / sélection de caches
  const [selectedCacheIds, setSelectedCacheIds] = useState([]);
  const [refreshingSelectedCaches, setRefreshingSelectedCaches] = useState(false);
  // const [togglingSelectedCaches, setTogglingSelectedCaches] = useState(false);
  const [selectedCachesRefreshReport, setSelectedCachesRefreshReport] = useState(null);

  // ---- Form (reset) ----
  const resetForm = () => {
  setSelectedConfig(null);
  setRefreshReport(null);
  setSelectedCachesRefreshReport(null);
  setSelectedCacheIds([]); // désélectionner les caches si la config est réinitialisée

  setForm({
    freshTtlSec: '',
    staleTtlSec: '',
    refreshMode: 'on_access',
    archived: false,
  });

  setWhatId('');
  setScope(DEFAULT_SCOPE);
  setCaches([]); // optionnel: pour vider la table en bas
  
};

  const currentConfig = selectedConfig; // source of truth

  const fetchConfigsList = async () => {
  setLoadingConfigs(true);
  try {
    const res = await api.getCacheConfigs({}); // 👈 PAS de filtre
    const data = Array.isArray(res.data) ? res.data : [];
    if (!isMountedRef.current) return;
    setConfigs(data);
  } catch (e) {
    console.error("Error fetching cache configs:", e);
    if (!isMountedRef.current) return;
    setConfigs([]);
  } finally {
    if (isMountedRef.current) setLoadingConfigs(false);
  }
};

const fetchCachesForSelection = async () => {
  const wid = selectedConfig?.what_ID || whatId;
  const sc = selectedConfig?.scope || scope;

  // si rien n’est sélectionné, on vide la table
  if (!wid) {
    setCaches([]);
    return;
  }

  setLoadingCaches(true);
  try {
    const res = await api.getCaches({
      what_ID: wid,
      scope: sc || DEFAULT_SCOPE,
      limit: 200,
      skip: 0,
    });
    const data = Array.isArray(res.data) ? res.data : [];
    if (!isMountedRef.current) return;
    setCaches(data);
  } catch (e) {
    console.error("Error fetching caches:", e);
    if (!isMountedRef.current) return;
    setCaches([]);
  } finally {
    if (isMountedRef.current) setLoadingCaches(false);
  }
};


  const fetchDefaultTtls = async () => {
  try {
    const res = await api.getDefaultCacheTtls({ scope });
    if (!isMountedRef.current) return;
    setDefaultTtls(res.data);
  } catch (e) {
    console.error("Error fetching default ttls:", e);
  }
};
//const { data, isLoading, error, fetchData } = useFetch(getAllV2Live, [provider]);
const fetchLives = async () => {
  try {
    const res = await api.getAllV2LiveInfo();
    // console.log("Fetched lives:", res.data);
    if (!isMountedRef.current) return;
    setLives(res.data);
  } catch (e) {
    console.error("Error fetching lives:", e);
  }
};

const liveOptions = useMemo(() => {
  const rows = Array.isArray(lives) ? lives : [];
  return rows.map(l => ({
    id: String(l._id),
    label: `${l.name || "(sans nom)"} — ${l.provider || "(sans provider)"}`,
    raw: l,
  }));
}, [lives]);

const liveById = useMemo(() => {
  const m = new Map();
  (Array.isArray(lives) ? lives : []).forEach((l) => {
    m.set(String(l._id), l);
  });
  return m;
}, [lives]);

const getLiveLabel = (what_ID) => {
  if (!what_ID) return "(live inconnu)";
  const live = liveById.get(String(what_ID));
  if (!live) return null; // important pour fallback
  return `${live.name || "(sans nom)"} — ${live.provider || "(sans provider)"}`;
};


  // refresh manuel (configs + caches + default ttls)
const refreshAll = async () => {
  await Promise.all([
    fetchConfigsList(),
    fetchCachesForSelection(),
    fetchDefaultTtls(),
    fetchLives(),
  ]);
  setSelectedCacheIds([]);
};

 const handleRefreshSelectedCaches = async () => {
    if (!selectedCacheIds.length) {
      alert("Sélectionnez au moins un cache.");
      return;
    }

    setRefreshingSelectedCaches(true);
    setSelectedCachesRefreshReport(null);

    try {
      const res = await api.refreshNowCaches(selectedCacheIds);

      setSelectedCachesRefreshReport(res?.data ?? null);

      await fetchCachesForSelection();
      setSelectedCacheIds([]);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error ?? "Erreur lors du rafraîchissement des caches sélectionnés.");
    } finally {
      setRefreshingSelectedCaches(false);
    }
  };

  const handleUpsert = async (e) => {
    e.preventDefault();

    if (!whatId) {
      alert('Veuillez renseigner un Live (what_ID).');
      return;
    }

    const payload = {
      what_ID: whatId,
      scope: scope || DEFAULT_SCOPE,
      refreshMode: form.refreshMode,
      archived: !!form.archived,
    };

    // TTLs : soit nombre, soit undefined (pour laisser null / défaut)
    if (String(form.freshTtlSec).trim() !== '') payload.freshTtlSec = Number(form.freshTtlSec);
    if (String(form.staleTtlSec).trim() !== '') payload.staleTtlSec = Number(form.staleTtlSec);

    try {
        await api.upsertCacheConfig(payload);

        // important : refetch la liste complète
        await fetchConfigsList();

        // et pour rafraîchir aussi le tableau de caches
        await fetchCachesForSelection();
        await fetchDefaultTtls();
        resetForm()
      } catch (e) {
      console.error('Error upserting cache config:', e);
      alert(e?.response?.data?.error ?? 'Erreur lors de la sauvegarde de la config cache.');
    }
  };

  //pour le moment tout les caches avec le what_id de la config active
const handleRefreshNow = async () => {
  if (!currentConfig?._id) return alert("Sélectionnez une configuration.");
  setRefreshingNow(true);
  setRefreshReport(null);
  try {
    const res = await api.refreshNowCacheConfig(currentConfig._id);
    setRefreshReport(res.data);     // {startedAt, endedAt, refreshed, ...}
    await refreshAll();
  } catch (e) {
    console.error(e);
    alert(e?.response?.data?.error ?? "Erreur lors du refresh immédiat.");
  } finally {
    setRefreshingNow(false);
  }
};

const handlePlanRefresh = async () => {
  if (!currentConfig?._id) return alert("Sélectionnez une configuration.");
  setPlanningRefresh(true);
  try {
    await api.forceRefreshCacheConfig(currentConfig._id);
    await refreshAll();
    resetForm();
  } catch (e) {
    console.error(e);
    alert(e?.response?.data?.error ?? "Erreur lors de la planification.");
  } finally {
    setPlanningRefresh(false);
  }
};

const handlePlanUnRefresh = async () => {
  if (!currentConfig?._id) return alert("Sélectionnez une configuration.");
  setPlanningRefresh(true);
  try {
    await api.forceUnrefreshCacheConfig(currentConfig._id);
    await refreshAll();
    resetForm();
  } catch (e) {
    console.error(e);
    alert(e?.response?.data?.error ?? "Erreur lors de la déplanification.");
  } finally {
    setPlanningRefresh(false);
  }
};

  const handleToggleArchive = async () => {
    if (!currentConfig?._id) {
      alert('Aucune config trouvée pour ce liveId/scope. Créez-la d’abord via "Enregistrer".');
      return;
    }
    try {
      if (currentConfig.archived) await api.unarchiveCacheConfig(currentConfig._id);
      else await api.archiveCacheConfig(currentConfig._id);
      await refreshAll();
      resetForm()
    } catch (e) {
      console.error('Error toggling archive:', e);
      alert(e?.response?.data?.error ?? 'Erreur lors du (un)archive.');
    }
  };

  const handleDeleteCache = async (cacheId, label) => {
    if (!cacheId) return;

     const confirmed = window.confirm(
        `Supprimer ce cache${label ? ` "${label}"` : ""} ?`
      );
      if (!confirmed) return;

    setDeletingCacheId(String(cacheId));
    try {
      await api.deleteCache(cacheId);

      setSelectedCacheIds((prev) => prev.filter((id) => id !== String(cacheId)));
      await fetchCachesForSelection();
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.error ?? "Erreur lors de la suppression du cache.");
    } finally {
      setDeletingCacheId(null);
    }
  };


  // IMAGES

const handleUploadHeaderImage = async (imageFile, options = {}) => {
  if (!currentConfig?._id || !imageFile) {
    alert("Sélectionnez d'abord une configuration existante.");
    return;
  }

  const formData = new FormData();
  formData.append('image', imageFile);

  if (options.ratio) {
    formData.append('ratio', String(options.ratio));
  }

  await api.uploadCacheConfigHeaderImage(currentConfig._id, formData);
};

const handleDeleteHeaderImage = async () => {
  if (!currentConfig?._id) return;

  try {
    const res = await api.deleteCacheConfigHeaderImage(currentConfig._id);

    setSelectedConfig((prev) => ({
      ...prev,
      ...res.data,
    }));

    await fetchConfigsList();
  } catch (e) {
    console.error(e);
    alert(e?.response?.data?.error ?? "Erreur lors de la suppression du bandeau.");
  }
};


  useEffect(() => {
  refreshAll();
  fetchLives();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


useEffect(() => {
  setSelectedCacheIds([]);
  setSelectedCachesRefreshReport(null);
  fetchCachesForSelection();
  fetchDefaultTtls();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedConfig]);


  const effectiveFreshTtl = Number.isFinite(+currentConfig?.freshTtlSec)
  ? +currentConfig.freshTtlSec
  : +defaultTtls.freshTtlSec;

const effectiveStaleTtl = Number.isFinite(+currentConfig?.staleTtlSec)
  ? +currentConfig.staleTtlSec
  : +defaultTtls.staleTtlSec;

const effectiveArchived = !!(currentConfig?.archived || defaultTtls?.archived);

const ttlSummary = useMemo(() => {
  return [
    ttlLine("OK (récent)", currentConfig?.freshTtlSec, defaultTtls?.freshTtlSec),
    ttlLine("À rafraîchir", currentConfig?.staleTtlSec, defaultTtls?.staleTtlSec),
  ];
}, [currentConfig, defaultTtls]);



const formatAge = (ageSec) => {
  if (!Number.isFinite(ageSec)) return '';
  if (ageSec < 60) return `${Math.floor(ageSec)}s`;
  if (ageSec < 3600) return `${Math.floor(ageSec / 60)}m`;
  return `${Math.floor(ageSec / 3600)}h`;
};

const computeStateLabel = (row) => {
  // archived
  if (effectiveArchived || row.archived) {
    return row.isProcessing ? "Archivé (rafraîchissement…)" : "Archivé";
  }

  // age
  const created = row?.createdAt ? new Date(row.createdAt).getTime() : null;
  if (!created) return row.isProcessing ? "Inconnu (rafraîchissement)" : "Inconnu";

  const ageSec = (Date.now() - created) / 1000;

  let base =
    ageSec < effectiveFreshTtl ? "OK (récent)"
    : ageSec < effectiveStaleTtl ? "A rafraîchir"
    : "Très ancien";

  if (row.isProcessing) base += " (rafraîchissement…)";
  return base;
};

const sortedCaches = useMemo(() => {
  const rows = [...(caches || [])].map((row) => {
    // console.log("row data:", row);
    const created = row?.createdAt ? new Date(row.createdAt).getTime() : null;
    const ageSec = created ? (Date.now() - created) / 1000 : null;
    const contextSummary = buildContextSummary(row);
    return {
      ...row,
      _context_summary: contextSummary,
      _ageSec: ageSec,
      _ageLabel: ageSec != null ? formatAge(ageSec) : '',
      _state: computeStateLabel(row),
    };
  });

  rows.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  return rows;
}, [caches, currentConfig, effectiveFreshTtl, effectiveStaleTtl]);


const configsWithLive = useMemo(() => {
  const rows = Array.isArray(configs) ? configs : [];

  return [...rows]
    .map((c) => ({
      ...c,
      _liveLabel: getLiveLabel(c.what_ID) || "",
    }))
    .sort((a, b) => a._liveLabel.localeCompare(b._liveLabel));
}, [configs, liveById]);

  const selectedCaches = useMemo(() => {
    const ids = new Set(selectedCacheIds);
    return sortedCaches.filter((row) => ids.has(row._id));
  }, [sortedCaches, selectedCacheIds]);

  const allVisibleCacheIds = useMemo(() => {
    return sortedCaches
      .map((row) => row._id ? String(row._id) : null)
      .filter(Boolean);
  }, [sortedCaches]);

  const allCachesSelected =
    allVisibleCacheIds.length > 0 &&
    allVisibleCacheIds.every((id) => selectedCacheIds.includes(id));

  const someCachesSelected =
    allVisibleCacheIds.some((id) => selectedCacheIds.includes(id)) && !allCachesSelected;

  const handleToggleOneCache = (cacheId) => {
    const id = String(cacheId);
    setSelectedCacheIds((prev) =>
      prev.includes(id)
        ? prev.filter((existingId) => existingId !== id)
        : [...prev, id]
    );
  };

  const handleToggleAllCaches = () => {
    setSelectedCacheIds((prev) => {
      if (allCachesSelected) return [];
      return allVisibleCacheIds;
    });
  };




  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h4">Cache Manager</Typography>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={refreshAll}>
          Rafraîchir la page
        </Button>
      </Stack>

    {/* Résumé des TTLs par défaut */}
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
      <Typography variant="subtitle1" gutterBottom>
        Cache global (valeurs par défaut)
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        Ces paramètres s’appliquent à tous les caches <strong>sans configuration spécifique</strong>.
      </Typography>

      <Stack spacing={0.5}>
        <Typography variant="body2">
          <strong>OK (récent) ({defaultTtls?.freshTtlSec}s)</strong> — durée pendant laquelle les données sont servies sans recalcul.
        </Typography>

        <Typography variant="body2">
          <strong>À rafraîchir ({defaultTtls?.staleTtlSec}s)</strong> — durée pendant laquelle les données peuvent être servies pendant un rafraîchissement en arrière-plan. Au delà, le cache est considéré comme "Très ancien" : seul le statut change pour information.
        </Typography>

        <Typography variant="body2">
          <strong>Mode ({prettyMode(defaultTtls?.refreshMode)})</strong> — stratégie déclenchant le recalcul du cache.
        </Typography>
      </Stack>

      {/* <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
        Source : {defaultTtls?.source || "unknown"} — valeurs fallback si aucune CacheConfig n’est définie.
      </Typography> */}
    </Paper>

      
      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Colonne gauche : Configuration du CacheConfig */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {`${selectedConfig ? 'Édition' : 'Création'} de configuration de cache`}
            </Typography>
            <Button
                type="button"
                variant="contained"
                onClick={() =>
                  resetForm()
                }
              >
                Réinitialiser le formulaire
              </Button>

            <Box sx={{ display: 'flex', gap: 2, mb: 2, mt: 4 }}>
              
              <FormControl size="small" fullWidth>
                <InputLabel>Live (V2)</InputLabel>
                <Select
                  label="Live (V2)"
                  value={whatId}
                  onChange={(e) => {
                    setSelectedConfig(null);
                    setRefreshReport(null);
                    setWhatId(e.target.value);
                  }}
                >
                  {(liveOptions || []).map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel>Scope</InputLabel>
                <Select
                  label="Scope"
                  value={scope}
                  onChange={(e) => setScope(e.target.value)}
                >
                  <MenuItem value="default">default</MenuItem>
                  <MenuItem value="calendar" disabled>calendrier</MenuItem> 
                  <MenuItem value="classement" disabled>classement</MenuItem> 
                  <MenuItem value="resultat" disabled>résultat</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box
              component="form"
              onSubmit={handleUpsert}
              sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <TextField
                label="Durée avant rafraîchissement (secondes)"
                value={form.freshTtlSec}
                onChange={(e) => setForm((s) => ({ ...s, freshTtlSec: e.target.value }))}
                size="small"
                placeholder="ex: 60"
              />
              <Typography variant="caption" color="text.secondary">
                Durée pendant laquelle les données sont servies sans recalcul. Laisser vide pour utiliser la valeur par défaut ({defaultTtls?.freshTtlSec}s).
              </Typography>
              </Box>
              {/* <TextField
                label="Durée avant Très ancien"
                value={form.staleTtlSec}
                onChange={(e) => setForm((s) => ({ ...s, staleTtlSec: e.target.value }))}
                size="small"
                placeholder="ex: 3600"
              /> */}

              {currentConfig && (
                <>
                  <Divider />

                  <Typography variant="subtitle2">
                    Image de bandeau du widget
                  </Typography>

                  <HeaderHandlerTool
                    initialImage={currentConfig.headerImageUrl}
                    uploadButtonLabel="Charger une image de bandeau"
                    onImageSave={handleUploadHeaderImage}
                    onImageDelete={handleDeleteHeaderImage}
                  />

                  {currentConfig.headerImageName && (
                    <Typography variant="caption" color="text.secondary">
                      Image S3 : {currentConfig.headerImageName}
                    </Typography>
                  )}
                </>
              )}

              

              <FormControl size="small">
                <InputLabel>refreshMode</InputLabel>
                <Select
                  label="refreshMode"
                  value={form.refreshMode}
                  onChange={(e) => setForm((s) => ({ ...s, refreshMode: e.target.value }))}
                >
                  <MenuItem value="on_access">on_access</MenuItem>
                  <MenuItem value="hourly" disabled>hourly</MenuItem>
                </Select>
              </FormControl>

              {!currentConfig && <Stack direction="row" alignItems="center" justifyContent="space-center">
                <Typography variant="body2">Archivé</Typography>
                <Switch
                  checked={!!form.archived}
                  onChange={(e) => setForm((s) => ({ ...s, archived: e.target.checked }))}
                />
              </Stack>}

              
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Actions
                  </Typography>

                  {currentConfig &&<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                    “Rafraîchir” recalculera immédiatement tous les caches liés (peut prendre quelques secondes).
                    “Planifier” recalculera au prochain accès utilisateur.
                  </Typography>}

                  <Stack direction="row" justifyContent="flex-start" gap={1} flexWrap="wrap">
                    <Button type="submit" variant="contained" disabled={loadingConfigs}>
                      Enregistrer
                    </Button>

                   {currentConfig && <> <Button
                      type="button"
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      onClick={handleRefreshNow}
                      disabled={!currentConfig?._id || !!currentConfig?.archived || refreshingNow}
                    >
                      Rafraîchir
                    </Button>

                    <Button
                      type="button"
                      variant="outlined"
                      startIcon={currentConfig?.forceRefresh ? <SyncDisabledIcon /> : <CachedIcon />}
                      onClick={currentConfig?.forceRefresh ? handlePlanUnRefresh : handlePlanRefresh}
                      disabled={!currentConfig?._id || !!currentConfig?.archived || planningRefresh}
                    >
                      {currentConfig?.forceRefresh ? 'Déplannifier' : 'Planifier'}
                    </Button>

                    <Button
                      type="button"
                      variant="outlined"
                      startIcon={currentConfig?.archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                      onClick={handleToggleArchive}
                      disabled={!currentConfig?._id}
                    >
                      {currentConfig?.archived ? 'Désarchiver' : 'Archiver'}
                    </Button></>}
                  </Stack>
                </Paper>
                {refreshReport && (
                  <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                    <Typography variant="subtitle2">Dernier rafraîchissement</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Début : {fmtDate(refreshReport.startedAt)} — Fin : {fmtDate(refreshReport.endedAt)}
                    </Typography>

                    <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                      <Chip size="small" label={`Total: ${refreshReport.total}`} />
                      <Chip size="small" color="success" label={`Rafraîchis: ${refreshReport.refreshed}`} />
                      <Chip size="small" color="warning" label={`Locks: ${refreshReport.skippedLocked}`} />
                      <Chip size="small" color="default" label={`Sans service: ${refreshReport.skippedNoService}`} />
                      <Chip size="small" color={refreshReport.errors ? "error" : "default"} label={`Erreurs: ${refreshReport.errors}`} />
                    </Stack>
                  </Paper>
                )}
          
            </Box>
          </Paper>
        </Grid>

        {/* Colonne droite : Liste des configurations */}
        <Grid item xs={12} md={6}>
          <Paper elevation={3} sx={{ p: 3 }}>

              <Typography variant="h6" gutterBottom>
                Configurations existantes ({configs.length})
              </Typography>

              <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Identifiant Live</TableCell>
                      <TableCell>Scope</TableCell>
                      <TableCell>Mode</TableCell>
                      <TableCell>Archivé</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {configsWithLive.map((c) => {
                      const isSelected = selectedConfig?._id === c._id;
                      //console.log("Rendering config row:", c, "isSelected:", isSelected);
                      return (
                        <TableRow
                          key={c._id}
                          hover
                          selected={isSelected}
                          sx={{ cursor: "pointer" }}
                          onClick={() => {
                            setSelectedConfig(c);

                            setWhatId(c.what_ID || "");
                            setScope(c.scope || DEFAULT_SCOPE);

                            setForm({
                              freshTtlSec: c.freshTtlSec ?? '',
                              staleTtlSec: c.staleTtlSec ?? '',
                              refreshMode: c.refreshMode ?? 'on_access',
                              archived: !!c.archived,
                            });

                            setRefreshReport(null);

                            // fetchCaches({ what_ID: c.what_ID, scope: c.scope });
                          }}
                        >
                          <TableCell>
                            {(() => {
                              const label = getLiveLabel(c.what_ID);

                              // si trouvé dans la map
                              if (label) {
                                return (
                                  <Stack spacing={0.25}>
                                    <Typography variant="body2">{label}</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                                      {c.what_ID}
                                    </Typography>
                                  </Stack>
                                );
                              }

                              // fallback (pas trouvé)
                              return (
                                <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                  {c.what_ID}
                                </Typography>
                              );
                            })()}
                          </TableCell>
                          <TableCell>{c.scope}</TableCell>
                          <TableCell>{prettyMode(c.refreshMode)}</TableCell>
                          <TableCell> <Chip size="small" color={`${c.archived ? 'success' : 'default'}`} label={`${c.archived ? 'oui' : 'non'}`} /> </TableCell>
                        </TableRow>
                      );
                    })}
                    {configs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          Aucune config.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

            <Divider sx={{ my: 2 }} />

            <Typography variant="subtitle2" gutterBottom>
              Configuration sélectionnée (détail)
            </Typography>

            {currentConfig ? (
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1.5}>
                  {/* Identité */}
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip label={`scope: ${currentConfig.scope || scope}`} size="small" />
                    <Chip label={`what_ID: ${currentConfig.what_ID || whatId}`} size="small" />
                    <Chip label={`mode: ${prettyMode(currentConfig.refreshMode ?? defaultTtls?.refreshMode)}`} size="small" />
                  </Stack>

                  {/* Flags */}
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    <Chip
                      label={currentConfig.archived ? "Archivé" : "Actif"}
                      color={currentConfig.archived ? "warning" : "success"}
                      size="small"
                    />
                    {currentConfig.forceRefresh && <Chip
                      label={"Rafraichissement en cours / planifié"}
                      color={"info"}
                      size="small"
                    />}
                    {currentConfig.forceRefreshAt &&<Typography variant="body2" color="text.secondary">
                      Dernier rafraichissement manuel: {currentConfig.forceRefreshAt ? fmtDate(currentConfig.forceRefreshAt) : "-"}
                    </Typography>}
                  </Stack>

                  {/* TTL effectifs */}
                  <Divider />

                  <Typography variant="subtitle2">TTL effectifs (timers)</Typography>
                  <Stack direction="row" spacing={2} flexWrap="wrap">
                    {ttlSummary.map((t) => (
                      <Chip
                        key={t.label}
                        label={`${t.label}: ${t.value}s (${t.origin})`}
                        size="small"
                        variant={t.origin === "override" ? "filled" : "outlined"}
                      />
                    ))}
                    {/* <Chip
                      label={`archived effectif: ${effectiveArchived ? "true" : "false"}`}
                      size="small"
                      variant={effectiveArchived ? "filled" : "outlined"}
                    /> */}
                  </Stack>

                  {/* Dates */}
                  <Divider />
                  <Typography variant="body2" color="text.secondary">
                    Date de création: {fmtDate(currentConfig.createdAt)} — Date de mise à jour: {fmtDate(currentConfig.updatedAt)}
                  </Typography>
                </Stack>
              </Paper>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Aucune configuration de cache trouvée (créez-en une nouvelle via “Enregistrer”).
              </Typography>
            )}
          </Paper>
        </Grid>
         </Grid>

        {/*En dessous : données des Caches de la configuration sélectionnée */}
       
          <Paper elevation={3} sx={{ p: 3, mt: 2 }} >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
                justifyContent="space-between"
                spacing={2}
                sx={{ mb: 2 }}
              >
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Données des caches
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {selectedConfig
                      ? `Identifiant Live: ${selectedConfig.what_ID}`
                      : "Sélectionner une configuration au dessus pour afficher ses caches."}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    {loadingCaches ? 'Chargement...' : `${sortedCaches.length} cache(s)`}
                    {selectedCacheIds.length > 0 ? ` — ${selectedCacheIds.length} sélectionné(s)` : ''}
                  </Typography>
                </Box>

                <Button
                  variant="contained"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefreshSelectedCaches}
                  disabled={!selectedConfig || !selectedCacheIds.length || refreshingSelectedCaches}
                >
                  Rafraîchir maintenant
                </Button>
              </Stack>

              {selectedCachesRefreshReport && (
              <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle2">
                  Dernier rafraîchissement unitaire
                </Typography>

                {Array.isArray(selectedCachesRefreshReport.details) &&
                  selectedCachesRefreshReport.details.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      {selectedCachesRefreshReport.details.slice(0, 5).map((d, idx) => (
                        <Typography key={idx} variant="caption" color="error" display="block">
                          {d.requestId || d.cacheId || 'Cache'} : {d.error}
                        </Typography>
                      ))}
                    </Box>
                )}

                <Typography variant="body2" color="text.secondary">
                  Début : {fmtDate(selectedCachesRefreshReport.startedAt)} — Fin : {fmtDate(selectedCachesRefreshReport.endedAt)}
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Durée : {fmtDuration(selectedCachesRefreshReport.durationMs)}
                </Typography>

                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1 }}>
                  <Chip size="small" label={`Demandés: ${selectedCachesRefreshReport.requested ?? 0}`} />
                  <Chip size="small" label={`Trouvés: ${selectedCachesRefreshReport.found ?? 0}`} />
                  <Chip size="small" color="success" label={`Rafraîchis: ${selectedCachesRefreshReport.refreshed ?? 0}`} />
                  <Chip size="small" color="warning" label={`Locks: ${selectedCachesRefreshReport.skippedLocked ?? 0}`} />
                  <Chip size="small" label={`Archivés: ${selectedCachesRefreshReport.skippedArchived ?? 0}`} />
                  <Chip size="small" label={`Sans service: ${selectedCachesRefreshReport.skippedNoService ?? 0}`} />
                  <Chip size="small" label={`Introuvables: ${selectedCachesRefreshReport.notFound ?? 0}`} />
                  <Chip
                    size="small"
                    color={(selectedCachesRefreshReport.errors ?? 0) > 0 ? "error" : "default"}
                    label={`Erreurs: ${selectedCachesRefreshReport.errors ?? 0}`}
                  />
                </Stack>
              </Paper>
            )}

            <TableContainer component={Paper} variant="outlined">
              {!selectedConfig ? (
                <Typography variant="body2" color="text.secondary">
                  Aucun cache affiché.
                </Typography>
              ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allCachesSelected}
                        indeterminate={someCachesSelected}
                        onChange={handleToggleAllCaches}
                        disabled={!selectedConfig || sortedCaches.length === 0}
                      />
                    </TableCell>
                    <TableCell>Données du cache</TableCell>
                    <TableCell>Scope</TableCell>
                    <TableCell>Rafraîchissement en cours</TableCell>
                    <TableCell>Date de dernier rafraîchissement</TableCell>
                    <TableCell>Etat</TableCell>
                    <TableCell>Age</TableCell>
                    <TableCell align="center">Supprimer</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                {sortedCaches.map((row) => {
                  const isChecked = !!row._id && selectedCacheIds.includes(String(row._id));

                  return (
                    <TableRow
                      key={row._id || row.requestId}
                      hover
                      selected={isChecked}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isChecked}
                          onChange={() => handleToggleOneCache(row._id)}
                          disabled={!row._id}
                        />
                      </TableCell>

                      <TableCell sx={{ minWidth: 220 }}>
                        <Stack spacing={0.5}>
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                            flexWrap="wrap"
                            alignContent="flex-start"
                          >
                            {row.contextLabel ? (
                              <Chip size="small" label={row.contextLabel} />
                            ) : (
                              <Chip size="small" variant="outlined" label="sans label" />
                            )}
                          </Stack>

                          {row._context_summary ? (
                            <Typography variant="caption" color="text.secondary">
                              {row._context_summary}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="text.secondary">
                              Aucun contexte enregistré
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>

                      <TableCell>{row.scope}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          color={row.isProcessing ? 'success' : 'default'}
                          label={row.isProcessing ? 'oui' : 'non'}
                        />
                      </TableCell>

                      <TableCell>
                        {row.createdAt ? new Date(row.createdAt).toLocaleString() : ''}
                      </TableCell>

                      <TableCell>{row._state}</TableCell>
                      <TableCell>{row._ageLabel}</TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          color="error"
                          title="Supprimer ce cache"
                          onClick={() => handleDeleteCache(row._id, row.contextLabel)}
                          disabled={!row._id || deletingCacheId === String(row._id) || row.isProcessing}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}

                {sortedCaches.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      Aucun cache trouvé.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
              </Table>
              )}
            </TableContainer>

            <Divider sx={{ my: 2 }} />

            {/* <Typography variant="caption" color="text.secondary">
              NB : “createdAt” correspond actuellement au timestamp utilisé pour l’âge TTL (il est mis à jour lors d’un updateCache).
            </Typography> */}
          </Paper>
        
     
    </Box>
  );
};

export default CacheManager;
